import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Button, Card, Alert, Row, Col, Container, ProgressBar } from 'react-bootstrap';
import authService from '../../shared/utils/authService';
import { EyeFill, EyeSlashFill, PiggyBankFill, ShieldLockFill, StarFill, GraphUpArrow } from 'react-bootstrap-icons';
import './Auth.css'; // Reuse Auth.css for consistent styling

const Signup = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [mobileNumber, setMobileNumber] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useNavigate();

    const handleSignup = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (password !== confirmPassword) {
            return setError('Passwords do not match.');
        }

        try {
            await authService.signup(email, password, firstName, lastName, mobileNumber);
            setSuccess('Account created successfully! Redirecting to login...');
            setTimeout(() => navigate('/login'), 2000);
        } catch (err) {
            if (err.response && err.response.data && err.response.data.detail) {
                setError(err.response.data.detail);
            } else {
                setError('Failed to create an account.');
            }
        }
    };

    const getPasswordStrength = () => {
        let score = 0;
        if (!password) return score;
        if (password.length > 8) score++;
        if (password.match(/[a-z]/)) score++;
        if (password.match(/[A-Z]/)) score++;
        if (password.match(/[0-9]/)) score++;
        if (password.match(/[^a-zA-Z0-9]/)) score++;
        return (score / 5) * 100;
    };

    const strength = getPasswordStrength();
    const progressColor = strength > 80 ? 'success' : strength > 50 ? 'warning' : 'danger';

    return (
        <Container fluid className="auth-page-bg">
            <Row className="w-100 min-vh-100 align-items-center justify-content-center">
                {/* Left Column: Marketing/Informative Section */}
                <Col lg={6} md={6} className="d-none d-md-flex auth-marketing-section">
                    <h1 className="display-4 mb-4">Start Your Journey to Financial Freedom.</h1>
                    <p className="lead mb-5">
                        Join thousands of users who are transforming their financial lives.
                        Sign up now to unlock powerful features and achieve lasting peace of mind.
                    </p>
                    <div className="marketing-mini-card-container">
                        <div className="marketing-mini-card">
                            <PiggyBankFill className="card-icon" />
                            <h5>Effortless Tracking</h5>
                            <p>Easily record and categorize every transaction with smart automation.</p>
                        </div>
                        <div className="marketing-mini-card">
                            <GraphUpArrow className="card-icon" />
                            <h5>Personalized Growth</h5>
                            <p>Get tailored insights and recommendations to achieve your financial goals.</p>
                        </div>
                        <div className="marketing-mini-card">
                            <StarFill className="card-icon" />
                            <h5>Premium Features</h5>
                            <p>Access advanced budgeting, custom rules, and exclusive analytics.</p>
                        </div>
                    </div>
                </Col>

                {/* Right Column: Signup Form */}
                <Col lg={4} md={8} sm={10} xs={12} className="auth-form-section">
                    <Card className="shadow-lg p-4 auth-card">
                        <Card.Body>
                            <div className="text-center mb-4">
                                <h1 className="text-primary mb-0">Expense Tracker</h1>
                                <p className="text-muted">Create Your Free Account</p>
                            </div>
                            {error && <Alert variant="danger">{error}</Alert>}
                            {success && <Alert variant="success">{success}</Alert>}
                            <Form onSubmit={handleSignup}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="form-floating mb-3" controlId="floatingFirstName">
                                            <Form.Control type="text" placeholder="First Name" required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                                            <Form.Label>First Name</Form.Label>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="form-floating mb-3" controlId="floatingLastName">
                                            <Form.Control type="text" placeholder="Last Name" required value={lastName} onChange={(e) => setLastName(e.target.value)} />
                                            <Form.Label>Last Name</Form.Label>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Group className="form-floating mb-3" controlId="floatingEmail">
                                    <Form.Control type="email" placeholder="name@example.com" required value={email} onChange={(e) => setEmail(e.target.value)} />
                                    <Form.Label>Email address</Form.Label>
                                </Form.Group>
                                <Form.Group className="form-floating mb-3" controlId="floatingMobileNumber">
                                    <Form.Control type="tel" placeholder="Mobile Phone Number (Optional)" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} />
                                    <Form.Label>Mobile Phone Number (Optional)</Form.Label>
                                </Form.Group>
                                <Form.Group className="form-floating mb-3 password-input-group" controlId="floatingPassword">
                                    <Form.Control type={showPassword ? "text" : "password"} placeholder="Password" required value={password} onChange={(e) => setPassword(e.target.value)} />
                                    <Form.Label>Password</Form.Label>
                                    <span className="password-toggle-icon" onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? <EyeSlashFill /> : <EyeFill />}
                                    </span>
                                </Form.Group>
                                {password && (
                                    <ProgressBar now={strength} variant={progressColor} style={{ height: '5px' }} className="mb-3" />
                                )}
                                <Form.Group className="form-floating mb-3 password-input-group" controlId="floatingConfirmPassword">
                                    <Form.Control type={showConfirmPassword ? "text" : "password"} placeholder="Confirm Password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                                    <Form.Label>Confirm Password</Form.Label>
                                    <span className="password-toggle-icon" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                        {showConfirmPassword ? <EyeSlashFill /> : <EyeFill />}
                                    </span>
                                </Form.Group>
                                <Form.Group className="mb-3" controlId="formBasicCheckbox">
                                    <Form.Check type="checkbox" required>
                                        <Form.Check.Input type="checkbox" required />
                                        <Form.Check.Label>
                                            I agree to the <Link to="/terms" target="_blank">Terms of Service</Link> and <Link to="/privacy" target="_blank">Privacy Policy</Link>
                                        </Form.Check.Label>
                                    </Form.Check>
                                </Form.Group>
                                <Button className="w-100 mt-3 btn-primary" type="submit">
                                    Create Account
                                </Button>
                            </Form>
                        </Card.Body>
                        <Card.Footer className="text-center auth-card-footer">
                            Already have an account? <Link to="/login">Log In</Link>
                        </Card.Footer>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Signup;
